import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2, Check, RefreshCw, ChevronDown, Lock, Images } from 'lucide-react';
import { api, capabilitiesCached } from '../../lib/api';
import type { AiImageGenerateResult } from '../../lib/types';

// Contexto de generación que el editor inyecta por campo `media` elegible.
// Sale del contrato de bloques (mediaRole/aspectRatio/promptHints en blocks.ts).
export interface AiImageContext {
  blockType?: string;
  fieldKey?: string;
  fieldLabel?: string;
  mediaRole?: string;
  aspectRatio?: string;
  promptHints?: string;
  pageId?: string | null;
}

interface Props {
  context: AiImageContext;
  // Coloca la imagen elegida en el campo (reemplaza o agrega según el picker).
  onPick: (url: string) => void;
}

interface Variant {
  url: string;
  alt: string | null;
}

const VARIANT_CHOICES = [1, 2, 4];

// OpenRouter no entrega progreso real de generación (el stream solo manda
// "procesando…" y luego la imagen de golpe), así que la barra es una estimación
// por tiempo. Calibrado en vivo: ~55 s por imagen con riverflow-fast. Las
// variantes se generan en serie, así que el total escala con la cantidad.
const ESTIMATE_PER_IMAGE_MS = 55_000;

// Pliega la dirección creativa base del campo + la instrucción del usuario en un
// solo prompt. La relación de aspecto se pasa como guía textual al modelo.
function buildPrompt(context: AiImageContext, instruction: string): string {
  const ratio = context.aspectRatio ? `Relación de aspecto aproximada ${context.aspectRatio}.` : '';
  return [context.promptHints?.trim(), instruction.trim(), ratio]
    .filter(Boolean)
    .join(' ')
    .trim();
}

export function AiImagePanel({ context, onPick }: Props) {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  // Por defecto se crea 1 imagen; el selector de cantidad va colapsado hasta que el
  // usuario decide explícitamente generar varias (cada una gasta cupo).
  const [variants, setVariants] = useState(1);
  const [showCount, setShowCount] = useState(false);
  const [loading, setLoading] = useState(false);
  // Progreso estimado (0–1) de la generación en curso. No es real (ver
  // ESTIMATE_PER_IMAGE_MS): avanza con el tiempo y solo llega a 1 cuando termina.
  const [progress, setProgress] = useState(0);
  const progressTimer = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<Variant[]>([]);
  const [quota, setQuota] = useState<{ used: number; limit: number } | null>(null);
  // null = aún sin cargar; true/false = el plan incluye imágenes IA o no.
  const [allowed, setAllowed] = useState<boolean | null>(null);

  // Al abrir por primera vez, resuelve el plan y el cupo actual (una sola vez).
  useEffect(() => {
    if (!open || allowed !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const caps = await capabilitiesCached();
        if (cancelled) return;
        setAllowed(caps.aiImages);
        if (caps.aiImages) {
          const usage = await api.ai.imageUsage();
          if (!cancelled) setQuota({ used: usage.used, limit: usage.limit });
        }
      } catch {
        // Si no podemos confirmar el plan, dejamos intentar: el backend manda.
        if (!cancelled) setAllowed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, allowed]);

  // Limpia el temporizador si el componente se desmonta a mitad de generación.
  useEffect(() => () => {
    if (progressTimer.current) clearInterval(progressTimer.current);
  }, []);

  function startProgress(count: number) {
    const estMs = ESTIMATE_PER_IMAGE_MS * Math.max(count, 1);
    const start = Date.now();
    setProgress(0.03);
    if (progressTimer.current) clearInterval(progressTimer.current);
    progressTimer.current = window.setInterval(() => {
      const elapsed = Date.now() - start;
      // Aproximación asintótica: rápido al inicio, desacelera y se topa en 0.95.
      // El 100% solo lo pone stopProgress cuando la imagen llega de verdad.
      const p = 1 - Math.exp(-elapsed / (estMs * 0.6));
      setProgress(Math.min(0.95, p));
    }, 300);
  }

  function stopProgress() {
    if (progressTimer.current) { clearInterval(progressTimer.current); progressTimer.current = null; }
    setProgress(1);
  }

  async function generate() {
    const prompt = buildPrompt(context, instruction);
    if (!prompt) {
      setError('Cuéntanos qué imagen quieres crear.');
      return;
    }
    setLoading(true);
    setError(null);
    startProgress(variants);
    try {
      const res: AiImageGenerateResult = await api.ai.image({
        prompt,
        blockType: context.blockType ?? null,
        fieldKey: context.fieldKey ?? null,
        purpose: context.mediaRole ?? 'block-image',
        altContext: context.fieldLabel ?? null,
        pageId: context.pageId ?? null,
        variants,
      });
      const picked = res.items.map((i) => ({ url: i.media.url, alt: i.media.altText }));
      setResults(picked);
      setQuota({ used: res.used, limit: res.limit });
      // Una sola imagen → se coloca de fondo al instante, sin el paso de "Usar esta".
      // Con varias, el usuario elige entre las opciones.
      if (picked.length === 1) onPick(picked[0].url);
    } catch (e) {
      setError((e as Error).message || 'No pudimos crear la imagen. Inténtalo de nuevo.');
    } finally {
      stopProgress();
      setLoading(false);
    }
  }

  const remaining = quota ? Math.max(quota.limit - quota.used, 0) : null;

  return (
    <div className="rounded-lg border border-dashed border-ui-border bg-ui-surface/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-ui-accent hover:text-ui-accent-hover transition-colors"
      >
        <span className="flex items-center gap-2">
          <Sparkles size={16} />
          Crear imagen con IA
        </span>
        <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            {allowed === null ? (
              <div className="px-3 pb-3 flex items-center gap-2 text-sm text-ui-text-subtle">
                <Loader2 size={16} className="animate-spin" /> Cargando…
              </div>
            ) : allowed === false ? (
              <div className="px-3 pb-3">
                <div className="flex items-start gap-2 rounded-md bg-ui-surface-2 px-3 py-2.5">
                  <Lock size={16} className="mt-0.5 flex-shrink-0 text-ui-text-muted" />
                  <p className="text-sm text-ui-text-muted">
                    La creación de imágenes con IA está disponible en el plan Pro.
                  </p>
                </div>
              </div>
            ) : (
            <div className="px-3 pb-3 space-y-3">
              <textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                rows={2}
                placeholder={
                  context.promptHints
                    ? 'Opcional: añade detalles (colores, ambiente, estilo)…'
                    : 'Describe la imagen que quieres crear…'
                }
                className="w-full px-3 py-2 text-sm rounded-md bg-ui-surface border border-ui-border text-ui-text placeholder:text-ui-text-subtle focus:outline-none focus:ring-2 focus:ring-ui-accent/40 resize-none"
              />

              {/* Resultados arriba de la pregunta de cantidad, para que lo recién
                  creado quede a la vista sin tener que desplazarse. Cuando es una
                  sola imagen ya quedó aplicada de fondo (sin clic): se marca como tal.
                  Con varias, cada tile invita a elegirla. */}
              {results.length > 0 && (
                <div className="grid gap-2 grid-cols-2">
                  {results.map((v) => (
                    <button
                      key={v.url}
                      type="button"
                      onClick={() => onPick(v.url)}
                      className="group relative aspect-video rounded-md overflow-hidden border border-ui-border hover:border-ui-accent transition-colors"
                    >
                      <img src={v.url} alt={v.alt ?? ''} className="w-full h-full object-cover" />
                      {results.length === 1 ? (
                        <span className="absolute top-1.5 left-1.5 flex items-center gap-1 rounded-full bg-ui-accent/90 px-2 py-0.5 text-ui-text-inverse text-sm font-medium">
                          <Check size={13} /> Aplicada
                        </span>
                      ) : (
                        <span className="absolute inset-0 flex items-center justify-center gap-1.5 bg-ui-accent/80 text-ui-text-inverse text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                          <Check size={16} /> Usar esta
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Cantidad: colapsada por defecto (se crea 1). El usuario expande solo
                  si quiere varias opciones, y ahí se le advierte que gasta cupo. */}
              {!showCount ? (
                <button
                  type="button"
                  onClick={() => setShowCount(true)}
                  className="flex items-center gap-1.5 text-sm text-ui-text-subtle hover:text-ui-text transition-colors"
                >
                  <Images size={14} /> Crear varias opciones a la vez
                </button>
              ) : (
                <div className="space-y-1.5">
                  <span className="block text-sm text-ui-text-subtle">¿Cuántas imágenes crear?</span>
                  <div className="flex items-center gap-1.5">
                    {VARIANT_CHOICES.map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setVariants(n)}
                        className={`w-9 h-9 rounded-md text-sm font-medium transition-colors ${
                          variants === n
                            ? 'bg-ui-accent text-ui-text-inverse'
                            : 'bg-ui-surface-2 text-ui-text-muted hover:text-ui-text'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-ui-text-subtle">
                    Cada imagen cuenta para tu cupo del mes, la elijas o no
                    {remaining !== null ? ` — te quedan ${remaining}` : ''}.
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={generate}
                disabled={loading}
                className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-md bg-ui-accent text-ui-text-inverse text-sm font-medium hover:bg-ui-accent-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading
                  ? <Loader2 size={16} className="animate-spin" />
                  : results.length > 0 ? <RefreshCw size={16} /> : <Sparkles size={16} />}
                {loading
                  ? 'Creando…'
                  : results.length > 0
                    ? 'Crear otras'
                    : variants > 1 ? `Crear ${variants}` : 'Crear'}
              </button>

              {loading && (
                <div className="space-y-1">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-ui-surface-2">
                    <motion.div
                      className="h-full rounded-full bg-ui-accent"
                      animate={{ width: `${Math.round(progress * 100)}%` }}
                      transition={{ ease: 'easeOut', duration: 0.3 }}
                    />
                  </div>
                  <p className="text-sm text-ui-text-subtle">
                    {variants === 1
                      ? 'Estamos creando tu imagen. Suele tardar cerca de un minuto.'
                      : `Estamos creando tus imágenes. Suele tardar unos ${Math.max(2, Math.round((ESTIMATE_PER_IMAGE_MS * variants) / 60000))} minutos.`}
                  </p>
                </div>
              )}

              {error && (
                <p className="text-sm text-ui-danger">{error}</p>
              )}

              {!showCount && remaining !== null && (
                <p className="text-sm text-ui-text-subtle">
                  {remaining > 0
                    ? `Te quedan ${remaining} imágenes este mes.`
                    : 'Has usado todas tus imágenes de este mes.'}
                </p>
              )}
            </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
